import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Badge,
  Spinner,
  Alert,
  InputGroup
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { marketplaceAPI, voiceAPI } from '../services/api';

function Marketplace() {
  const [models, setModels] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Search filters
  const [searchText, setSearchText] = useState('');
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [priceRange, setPriceRange] = useState(null);
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (categories) {
      performSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, currentPage]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [featuredRes, categoriesRes, modelsRes] = await Promise.all([
        marketplaceAPI.getFeatured(),
        marketplaceAPI.getCategories(),
        voiceAPI.getModels({ page: 1, per_page: 20 })
      ]);

      setFeatured(featuredRes.data);
      setCategories(categoriesRes.data);
      setModels(modelsRes.data.models || []);
      setTotalPages(modelsRes.data.pages || 1);
    } catch (err) {
      console.error('Marketplace fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async () => {
    try {
      setSearching(true);

      const searchParams = {
        page: currentPage,
        per_page: 20,
        sort_by: sortBy,
      };

      if (searchText) searchParams.q = searchText;
      if (selectedLanguages.length > 0) searchParams.languages = selectedLanguages;
      if (selectedGenders.length > 0) searchParams.genders = selectedGenders;
      if (selectedStyles.length > 0) searchParams.styles = selectedStyles;
      if (priceRange) searchParams.price_range = priceRange;
      if (minRating > 0) searchParams.min_rating = minRating;

      const response = await marketplaceAPI.search(searchParams);
      setModels(response.data.models || []);
      setTotalPages(response.data.pages || 1);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.response?.data?.error || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    performSearch();
  };

  const clearFilters = () => {
    setSearchText('');
    setSelectedLanguages([]);
    setSelectedGenders([]);
    setSelectedStyles([]);
    setPriceRange(null);
    setMinRating(0);
    setCurrentPage(1);
    performSearch();
  };

  const toggleFilter = (value, selected, setSelected) => {
    if (selected.includes(value)) {
      setSelected(selected.filter(v => v !== value));
    } else {
      setSelected([...selected, value]);
    }
  };

  if (loading) {
    return (
      <Container className="text-center" style={{ marginTop: '100px' }}>
        <Spinner animation="border" />
        <p className="mt-3">Loading marketplace...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="mb-4">Voice Model Marketplace</h2>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Featured Models */}
      {featured && !searchText && currentPage === 1 && (
        <div className="mb-5">
          <h4 className="mb-3">Featured Models</h4>
          <Row>
            {featured.top_rated?.slice(0, 3).map((model) => (
              <Col md={4} key={model.id} className="mb-3">
                <Card className="h-100 border-primary">
                  <Card.Body>
                    <Badge bg="warning" className="mb-2">Top Rated</Badge>
                    <h5>{model.name}</h5>
                    <p className="text-muted small">{model.description?.substring(0, 80)}...</p>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-primary fw-bold">${model.base_price}</span>
                      <span>{model.rating?.toFixed(1) || 'N/A'} ⭐</span>
                    </div>
                    <Button
                      variant="primary"
                      size="sm"
                      className="w-100"
                      onClick={() => navigate(`/model/${model.id}`)}
                    >
                      View Details
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <Row>
        {/* Filters Sidebar */}
        <Col md={3}>
          <Card className="mb-3 sticky-top" style={{ top: '20px' }}>
            <Card.Body>
              <h5 className="mb-3">Filters</h5>

              {/* Search */}
              <Form onSubmit={handleSearch}>
                <Form.Group className="mb-3">
                  <Form.Label>Search</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search models..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Button variant="outline-secondary" type="submit" disabled={searching}>
                      {searching ? <Spinner animation="border" size="sm" /> : '🔍'}
                    </Button>
                  </InputGroup>
                </Form.Group>

                {/* Language Filter */}
                {categories?.languages && (
                  <Form.Group className="mb-3">
                    <Form.Label>Language</Form.Label>
                    <div>
                      {categories.languages.map((lang) => (
                        <Form.Check
                          key={lang}
                          type="checkbox"
                          label={lang}
                          checked={selectedLanguages.includes(lang)}
                          onChange={() => toggleFilter(lang, selectedLanguages, setSelectedLanguages)}
                        />
                      ))}
                    </div>
                  </Form.Group>
                )}

                {/* Gender Filter */}
                {categories?.genders && (
                  <Form.Group className="mb-3">
                    <Form.Label>Gender</Form.Label>
                    <div>
                      {categories.genders.map((gender) => (
                        <Form.Check
                          key={gender}
                          type="checkbox"
                          label={gender}
                          checked={selectedGenders.includes(gender)}
                          onChange={() => toggleFilter(gender, selectedGenders, setSelectedGenders)}
                        />
                      ))}
                    </div>
                  </Form.Group>
                )}

                {/* Style Filter */}
                {categories?.styles && categories.styles.length > 0 && (
                  <Form.Group className="mb-3">
                    <Form.Label>Style</Form.Label>
                    <div>
                      {categories.styles.slice(0, 5).map((style) => (
                        <Form.Check
                          key={style}
                          type="checkbox"
                          label={style}
                          checked={selectedStyles.includes(style)}
                          onChange={() => toggleFilter(style, selectedStyles, setSelectedStyles)}
                        />
                      ))}
                    </div>
                  </Form.Group>
                )}

                {/* Rating Filter */}
                <Form.Group className="mb-3">
                  <Form.Label>Minimum Rating</Form.Label>
                  <Form.Select
                    value={minRating}
                    onChange={(e) => setMinRating(Number(e.target.value))}
                  >
                    <option value="0">Any Rating</option>
                    <option value="3">3+ ⭐</option>
                    <option value="4">4+ ⭐</option>
                    <option value="4.5">4.5+ ⭐</option>
                  </Form.Select>
                </Form.Group>

                {/* Price Range */}
                {categories?.price_ranges && (
                  <Form.Group className="mb-3">
                    <Form.Label>Price Range</Form.Label>
                    <Form.Select
                      onChange={(e) => {
                        const selected = categories.price_ranges[e.target.value];
                        setPriceRange(selected ? { min: selected.min, max: selected.max } : null);
                      }}
                    >
                      <option value="">Any Price</option>
                      {categories.price_ranges.map((range, idx) => (
                        <option key={idx} value={idx}>
                          {range.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}

                <div className="d-grid gap-2">
                  <Button variant="primary" type="submit" disabled={searching}>
                    Apply Filters
                  </Button>
                  <Button variant="outline-secondary" onClick={clearFilters}>
                    Clear All
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Models Grid */}
        <Col md={9}>
          {/* Sort Options */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0">{models.length} models found</p>
            <Form.Select
              style={{ width: '200px' }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="popular">Most Popular</option>
              <option value="rating">Highest Rated</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
            </Form.Select>
          </div>

          {/* Models List */}
          {searching ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3">Searching...</p>
            </div>
          ) : models.length === 0 ? (
            <Card className="text-center p-5">
              <Card.Body>
                <h5>No models found</h5>
                <p className="text-muted">Try adjusting your filters or search terms</p>
                <Button variant="primary" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <>
              <Row>
                {models.map((model) => (
                  <Col md={6} lg={4} key={model.id} className="mb-4">
                    <Card className="h-100">
                      <Card.Body>
                        <h6>{model.name}</h6>
                        <p className="text-muted small mb-2">
                          {model.description?.substring(0, 80)}...
                        </p>
                        <div className="mb-2">
                          {model.language && <Badge bg="secondary" className="me-1">{model.language}</Badge>}
                          {model.gender && <Badge bg="info" className="me-1">{model.gender}</Badge>}
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="text-primary fw-bold">${model.base_price}</span>
                          <span className="text-muted small">
                            {model.rating?.toFixed(1) || 'N/A'} ⭐ ({model.rating_count || 0})
                          </span>
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="w-100"
                          onClick={() => navigate(`/model/${model.id}`)}
                        >
                          View Details
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                    className="me-2"
                  >
                    Previous
                  </Button>
                  <span className="align-self-center mx-3">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline-primary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}

export default Marketplace;
